import { Link } from "react-router-dom"
import Seo from "@/components/Seo"
import { useState } from "react"

const IMGS = {
  hero: "/website-assets/images/svc-hero-racks-1600x900.jpg",
  network: "/website-assets/images/svc-network-900x600.jpg",
  cloud: "/website-assets/images/svc-cloud-900x600.jpg",
  cyber: "/website-assets/images/svc-cyber-900x600.jpg",
  consulting: "/website-assets/images/svc-consulting-900x600.jpg",
  hardware: "/website-assets/images/svc-hardware-900x600.jpg",
  managed: "/website-assets/images/svc-managed-900x600.jpg",
  cctv: "/website-assets/images/svc-cctv-900x600.jpg",
  training: "/website-assets/images/svc-training-900x600.jpg",
  web: "/website-assets/images/svc-web-900x600.jpg",
}

const PARTNER_SOLUTIONS = [
  {
    brand: "Fortinet",
    logo: "/website-assets/brands/fortinet.svg",
    title: "FortiGate Next-Generation Firewalls",
    points: ["Fortinet Security Fabric across firewall, Wi-Fi & endpoints", "FortiGuard IPS, web filtering & anti-ransomware", "Secure SD-WAN and VPN built into every FortiGate", "24/7 managed firewall monitoring from Jero-One"],
  },
  {
    brand: "VeloCloud",
    logo: "/website-assets/brands/velocloud.png",
    title: "VeloCloud SD-WAN & SASE",
    points: ["Combine fibre, broadband & 4G/5G into one resilient link", "Automatic failover and application-aware routing", "Cloud-delivered security (SASE) for remote users", "Zero-touch branch deployment and central orchestration"],
  },
]

function SecureNetworkSolutions() {
  return (
    <section className="bg-[#0A0F0B] px-6 py-20 md:px-14" data-testid="secure-network-solutions">
      <div className="mx-auto max-w-7xl">
        <div className="mb-3 font-mono text-xs tracking-widest text-[#2A7D4A]">CYBERSECURITY × NETWORK INFRASTRUCTURE</div>
        <h2 className="font-display text-5xl font-black text-[#F4F6F0] md:text-6xl">SECURE NETWORK SOLUTIONS</h2>
        <p className="mt-4 max-w-2xl text-[#6B7C6E]">As Fortinet and VeloCloud solutions partners, we design, install and manage secure networks that connect your head office, branches and cloud, for banks, schools, hospitals, NGOs and growing businesses.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {PARTNER_SOLUTIONS.map((p) => (
            <div key={p.brand} className="rounded-3xl border border-[#1B5E35]/25 bg-[#111814] p-8 transition-colors hover:border-[#1B5E35]" data-testid={`partner-solution-${p.brand.toLowerCase()}`}>
              <img src={p.logo} alt={`${p.brand} logo`} className="h-8 w-auto max-w-[180px] object-contain brightness-0 invert" loading="lazy" />
              <h3 className="mt-6 text-2xl font-bold text-[#F4F6F0]">{p.title}</h3>
              <ul className="mt-4 space-y-2">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-2 text-sm text-[#F4F6F0]/80"><span className="text-[#2A7D4A]">▸</span>{pt}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/shop/secure-sdwan" className="rounded-xl bg-[#1B5E35] px-7 py-3.5 font-bold text-[#F4F6F0] transition-colors hover:bg-[#2A7D4A]" data-testid="secure-network-shop-btn">View FortiGate & VeloCloud models →</Link>
          <Link to="/shop/interactive-conferencing" className="rounded-xl border border-[#1B5E35]/40 px-7 py-3.5 font-bold text-[#F4F6F0] transition-colors hover:border-[#1B5E35]" data-testid="conferencing-shop-btn">Interactive boards & conference rooms →</Link>
        </div>
      </div>
    </section>
  )
}

const SERVICES = [
  {
    id: "network",
    icon: "🌐",
    title: "Network Infrastructure",
    tagline: "The backbone of your business",
    img: IMGS.network,
    desc: "We design, deploy, and manage enterprise-grade network infrastructure that keeps your business connected reliably and securely — from local campuses to cross-border wide-area networks across East Africa and North America.",
    features: [
      "Fortinet FortiGate & VeloCloud SD-WAN design and installation",
      "Interactive boards & video-conference room setup (BenQ, Barco, Yealink)",
      "LAN/WAN design and deployment",
      "Fiber optic installation",
      "Wireless enterprise networks (Wi-Fi 6/6E)",
      "SD-WAN and MPLS solutions",
      "Network monitoring and management",
      "Disaster recovery and redundancy planning",
    ],
  },
  {
    id: "cloud",
    icon: "☁️",
    title: "Cloud Solutions",
    tagline: "Scale without limits",
    img: IMGS.cloud,
    desc: "From cloud migration strategy to fully managed multi-cloud environments, we help organizations in Minnesota, Uganda, and Canada leverage AWS, Azure, and Google Cloud to reduce costs and increase agility.",
    features: [
      "Cloud readiness assessment",
      "AWS, Azure, and GCP migrations",
      "Hybrid and multi-cloud architecture",
      "Cloud cost optimization",
      "Managed cloud operations",
      "Cloud-native application development",
    ],
  },
  {
    id: "cybersecurity",
    icon: "🔒",
    title: "Cybersecurity",
    tagline: "Protect what matters most",
    img: IMGS.cyber,
    desc: "Our security experts provide end-to-end protection from threat detection to incident response. We serve government agencies, financial institutions, and enterprises across three continents.",
    features: [
      "Fortinet Security Fabric & managed FortiGate firewalls",
      "Security Operations Center (SOC)",
      "Penetration testing and vulnerability assessments",
      "SIEM implementation and management",
      "Compliance (ISO 27001, SOC 2, GDPR, NIST)",
      "Endpoint detection and response (EDR)",
      "Security awareness training",
    ],
  },
  {
    id: "consulting",
    icon: "💡",
    title: "IT Consulting",
    tagline: "Strategy that drives results",
    img: IMGS.consulting,
    desc: "Our consultants work alongside your leadership team to build technology roadmaps, manage digital transformation, and ensure every IT investment delivers measurable business value.",
    features: [
      "Digital transformation strategy",
      "IT architecture review and design",
      "Technology roadmap development",
      "IT governance and policy frameworks",
      "Vendor selection and procurement advisory",
      "CTO/CIO as-a-service",
    ],
  },
  {
    id: "hardware",
    icon: "🖥️",
    title: "Hardware Supply",
    tagline: "Certified. Reliable. Delivered.",
    img: IMGS.hardware,
    desc: "We source, configure, and deploy enterprise hardware from leading global vendors — laptops, servers, networking equipment, and peripherals — with full warranty support and lifecycle management.",
    features: [
      "Dell, HP, Cisco, Fortinet, and more",
      "Server rack configuration and deployment",
      "Laptop and desktop procurement",
      "Hardware lifecycle management",
      "Warranty and support coordination",
      "Asset tracking and disposal",
    ],
  },
  {
    id: "managed",
    icon: "🛠️",
    title: "Managed IT Services",
    tagline: "Your IT team, fully managed",
    img: IMGS.managed,
    desc: "Let us handle your entire IT operation so you can focus on your core business. Our managed services include 24/7 monitoring, helpdesk support, patch management, and proactive maintenance.",
    features: [
      "24/7 remote monitoring and management",
      "Helpdesk and end-user support",
      "Patch management and updates",
      "Backup and disaster recovery",
      "IT documentation and reporting",
      "Monthly performance reviews",
    ],
  },
  {
    id: "connectivity",
    icon: "↗",
    title: "ISP & Connectivity",
    tagline: "Reliable access everywhere",
    img: IMGS.network,
    desc: "We assess, install, and optimize business internet, LAN, WAN, and Wi-Fi connectivity for offices, campuses, and distributed operations.",
    features: [
      "Business internet assessment and setup",
      "Fiber and wireless connectivity",
      "Campus and branch connectivity",
      "Bandwidth planning and optimization",
      "Failover and link redundancy",
      "Ongoing network performance support",
    ],
  },
  {
    id: "cctv",
    icon: "◉",
    title: "CCTV & Surveillance",
    tagline: "See and secure every site",
    img: IMGS.cctv,
    desc: "End-to-end IP and analogue surveillance systems from Hikvision, Dahua, Axis, and Hanwha, with storage, remote monitoring, and maintenance.",
    features: [
      "IP and analogue camera supply",
      "NVR and DVR configuration",
      "Remote mobile monitoring",
      "Access control integration",
      "Site surveys and system design",
      "Preventive maintenance",
    ],
  },
  {
    id: "data-centre",
    icon: "▤",
    title: "Data Centre Support",
    tagline: "Critical infrastructure, managed",
    img: IMGS.network,
    desc: "Design, deployment, monitoring, and maintenance for enterprise data centres, server rooms, and business-critical infrastructure.",
    features: [
      "Data centre design and deployment",
      "Server administration",
      "Rack, power, and cooling planning",
      "24/7 monitoring and incident response",
      "Preventive maintenance",
      "Business continuity planning",
    ],
  },
  {
    id: "backup",
    icon: "⟳",
    title: "Backup & Anti-Virus",
    tagline: "Recover fast, stay protected",
    img: IMGS.cyber,
    desc: "Secure data conversion, backup, endpoint protection, security audits, and disaster recovery with confidentiality at the core.",
    features: [
      "Cloud and local backup",
      "Enterprise antivirus licensing",
      "Endpoint security deployment",
      "Disaster recovery planning",
      "Data migration and conversion",
      "Security audits",
    ],
  },
  {
    id: "projects",
    icon: "◇",
    title: "IT Project Management",
    tagline: "From requirements to results",
    img: IMGS.consulting,
    desc: "A proven delivery process covering requirements, solution design, procurement, implementation, testing, training, and handover.",
    features: [
      "Requirements and site assessment",
      "Solution architecture and budgeting",
      "Vendor and procurement management",
      "Deployment coordination",
      "Testing and quality assurance",
      "Training and project handover",
    ],
  },
  {
    id: "software",
    icon: "</>",
    title: "Software Development",
    tagline: "Systems built around your work",
    img: IMGS.web,
    desc: "Custom software, systems integration, re-engineering, management information systems, and web-based products built for real operations.",
    features: [
      "Custom web applications",
      "Management information systems",
      "Systems integration",
      "Workflow automation",
      "Legacy system modernization",
      "Support and continuous improvement",
    ],
  },
  {
    id: "training",
    icon: "A+",
    title: "IT Education & Training",
    tagline: "Practical skills that transfer",
    img: IMGS.training,
    desc: "Corporate, project-based, and technology-based IT training delivered on-site, remotely, or through dedicated learning centres.",
    features: [
      "Corporate instructor-led training",
      "Project-based learning",
      "Self-paced technology training",
      "Corporate learning centres",
      "Customized course content",
      "Individual and team programs",
    ],
  },
  {
    id: "websites",
    icon: "WWW",
    title: "Web & E-Commerce",
    tagline: "Launch, sell, and grow online",
    img: IMGS.web,
    desc: "One team for responsive website design, online stores, hosting, domains, analytics, SEO, security, and ongoing support.",
    features: [
      "Business website design",
      "E-commerce development",
      "Domain and hosting setup",
      "Search engine optimization",
      "Analytics and conversion tracking",
      "Maintenance and content support",
    ],
  },
]

export function Services() {
  const [active, setActive] = useState(SERVICES[0].id)
  const svc = SERVICES.find((s) => s.id === active)

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif" }}>
      <Seo title="Managed IT, Cybersecurity & Network Services | Jero-One" description="Network infrastructure, Fortinet & SD-WAN security, cloud migration, CCTV, data centre and managed IT services in Kampala, Entebbe, Minnesota and Canada." path="/services" />
      {/* Hero */}
      <section className="grid md:grid-cols-2 min-h-[50vh]">
        <div className="bg-[#0A0F0B] flex flex-col justify-center px-8 md:px-16 py-16">
          <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-4">
            OUR SERVICES
          </div>
          <h1
            className="text-6xl font-black text-[#F4F6F0] leading-none mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            FULL-SPECTRUM
            <br />
            <span style={{ color: "#1B5E35" }}>IT EXPERTISE</span>
          </h1>
          <p className="text-[#6B7C6E] leading-relaxed max-w-md">
            From network cabling to cloud architecture, we cover every layer of
            your technology stack — delivered by certified engineers across the
            USA, Uganda, and Canada.
          </p>
        </div>
        <div className="relative bg-[#111A14] overflow-hidden min-h-48">
          <img fetchPriority="high"
            src={IMGS.hero}
            alt="Modern network racks with patched switches"
            data-testid="services-hero-image"
            className="w-full h-full object-cover opacity-60"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, #0A0F0B 0%, transparent 30%)",
            }}
          />
        </div>
      </section>

      {/* Service Tabs + Detail */}
      <section className="py-20 px-6 md:px-14 bg-[#0A0F0B]">
        <div className="max-w-7xl mx-auto">
          {/* Tab pills */}
          <div className="flex flex-wrap gap-2 mb-12">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  active === s.id
                    ? "bg-[#1B5E35] text-[#F4F6F0]"
                    : "bg-[#111A14] text-[#6B7C6E] hover:text-[#F4F6F0] border border-[#1B5E35]/10"
                }`}
              >
                <span>{s.icon}</span> {s.title}
              </button>
            ))}
          </div>

          {/* Active service detail */}
          <div className="grid md:grid-cols-2 gap-0 border border-[#1B5E35]/15 rounded-2xl overflow-hidden">
            <div className="relative overflow-hidden min-h-72">
              <img loading="lazy" decoding="async"
                src={svc.img}
                alt={svc.title}
                className="w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, transparent 50%, #0A0F0B 100%), linear-gradient(to top, rgba(10,15,11,0.7) 0%, transparent 50%)",
                }}
              />
              <div className="absolute bottom-6 left-6">
                <div className="text-4xl">{svc.icon}</div>
              </div>
            </div>
            <div className="bg-[#0A0F0B] p-10 md:p-12 flex flex-col justify-center">
              <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-2">
                {svc.tagline.toUpperCase()}
              </div>
              <h2
                className="text-4xl font-black text-[#F4F6F0] mb-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {svc.title.toUpperCase()}
              </h2>
              <p className="text-[#6B7C6E] leading-relaxed mb-8">{svc.desc}</p>
              <ul className="grid grid-cols-1 gap-2 mb-8">
                {svc.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-sm text-[#F4F6F0]/80"
                  >
                    <span className="text-[#1B5E35] mt-0.5 flex-shrink-0">
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="self-start bg-[#1B5E35] hover:bg-[#2A7D4A] text-[#F4F6F0] font-bold px-8 py-3 rounded-lg transition-colors text-sm"
              >
                Request This Service →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us strip */}
      <SecureNetworkSolutions />

      <section className="bg-[#111A14] border-t border-[#1B5E35]/10 py-16 px-6 md:px-14">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs text-[#1B5E35] font-mono tracking-widest mb-3">
              WHY JERO-ONE
            </div>
            <h2
              className="text-4xl font-black text-[#F4F6F0]"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              THE JERO-ONE ADVANTAGE
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: "🌍",
                title: "3 Continents",
                desc: "US, African, and Canadian presence means we understand local challenges everywhere.",
              },
              {
                icon: "⚡",
                title: "Fast Response",
                desc: "Average ticket response under 2 hours. Critical issues resolved in under 4 hours.",
              },
              {
                icon: "🎓",
                title: "Certified Engineers",
                desc: "Cisco, Microsoft, AWS, CompTIA certified professionals on every project.",
              },
              {
                icon: "🤝",
                title: "Long-term Partner",
                desc: "We build lasting relationships, not transactional engagements.",
              },
            ].map((w) => (
              <div
                key={w.title}
                className="bg-[#0A0F0B] border border-[#1B5E35]/10 rounded-2xl p-7 text-center hover:border-[#1B5E35]/40 transition-colors"
              >
                <div className="text-3xl mb-3">{w.icon}</div>
                <div className="font-bold text-[#F4F6F0] mb-2">{w.title}</div>
                <p className="text-[#6B7C6E] text-sm leading-relaxed">
                  {w.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B5E35] py-16 px-6 md:px-14">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2
              className="text-4xl font-black text-[#F4F6F0] mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              NOT SURE WHICH SERVICE YOU NEED?
            </h2>
            <p className="text-[#F4F6F0]/70">
              Book a free consultation and our experts will assess your needs.
            </p>
          </div>
          <Link
            to="/contact"
            className="flex-shrink-0 bg-[#F4F6F0] text-[#1B5E35] font-bold px-10 py-4 rounded-xl hover:bg-white transition-colors text-sm whitespace-nowrap"
          >
            Talk to an Expert
          </Link>
        </div>
      </section>
    </div>
  )
}
